import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const TX_TYPES = {
  income:       { label: 'Gelir',          color: '#16a34a', sign:  1 },
  expense:      { label: 'Gider',          color: '#dc2626', sign: -1 },
  transfer_in:  { label: 'Transfer Giriş', color: '#0284c7', sign:  1 },
  transfer_out: { label: 'Transfer Çıkış', color: '#d97706', sign: -1 },
};

const today = new Date().toISOString().substring(0, 10);
// Varsayılan tarih filtresi: ayın 1'inden bugüne. Sadece "today→today" çok kısıtlayıcıydı
// (yeni yazılan hareket bile tarih konvansiyonuna takılırsa görünmüyordu).
const monthStart = (() => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().substring(0, 10);
})();

const emptyForm = {
  transaction_date: today,
  description: '', amount: '', reference_no: '', notes: '',
  register_id: '', transaction_type: 'income',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n, cur) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + (cur ? ' ' + cur : '');
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

export default function CashMovementsPage() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: monthStart, date_to: today, register_id: '', tx_type: '' });

  const [modalMode, setModalMode] = useState(null); // null | 'view' | 'add'
  const [selectedTx, setSelectedTx] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [settings, setSettings] = useState({});
  const [personnel, setPersonnel] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [showQuickList, setShowQuickList] = useState(null);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/cash-registers/all-transactions', { params });
      if (res.data.success) {
        setTransactions(res.data.data || []);
        setSummary(res.data.summary || null);
      }
    } catch (err) {
      console.error('cash all-transactions error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [filters]);

  const fetchRegisters = useCallback(async () => {
    try {
      const res = await api.get('/cash-registers');
      if (res.data.success) setRegisters((res.data.data.items || []).filter(r => r.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);
  useEffect(() => {
    api.get('/settings').then(r => { if (r.data.success) setSettings(r.data.data); }).catch(() => {});
    api.get('/personnel', { params: { limit: 200 } }).then(r => {
      if (r.data.success) setPersonnel((r.data.data.items || []).filter(p => p.is_active));
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const openAdd = () => {
    const defId = settings.default_cash_register_id && registers.find(r => r.id === settings.default_cash_register_id)
      ? settings.default_cash_register_id
      : (registers[0]?.id || '');
    setForm({ ...emptyForm, register_id: filters.register_id || defId });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const openView = (tx) => { setSelectedTx(tx); setMsg(null); setModalMode('view'); };

  const handleSave = async () => {
    const { register_id, transaction_date, description, transaction_type, amount } = form;
    if (!register_id) return setMsg({ type: 'error', text: 'Kasa secin.' });
    if (!transaction_date || !description || !amount) return setMsg({ type: 'error', text: 'Tarih, aciklama ve tutar zorunludur.' });
    setSaving(true); setMsg(null);
    try {
      await api.post('/cash-registers/' + register_id + '/transactions', {
        transaction_date, description, transaction_type,
        amount: parseFloat(amount),
        reference_no: form.reference_no || null,
        notes: form.notes || null,
        personnel_id: form.personnel_id || null,
        special_code1: form.special_code1 || null,
        special_code2: form.special_code2 || null,
      });
      closeModal();
      await fetchTransactions();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const tf = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const filtered = transactions.filter(tx =>
    !search ||
    tx.description?.toLowerCase().includes(search.toLowerCase()) ||
    tx.register_name?.toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference_no || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kasa Hareketleri"
        count={filtered.length}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Hareket</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'register_id', type: 'select', label: 'Kasa', flex: '1 1 220px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Kasalar' }, ...registers.map(r => ({ value: r.id, label: `${r.register_name} (${r.currency_code})` }))] },
          { key: 'tx_type', type: 'select', label: 'Hareket Tipi', minWidth: 160,
            options: [
              { value: '', label: 'Tümü' },
              { value: 'income', label: 'Gelir' },
              { value: 'expense', label: 'Gider' },
              { value: 'transfer_in', label: 'Transfer Giriş' },
              { value: 'transfer_out', label: 'Transfer Çıkış' },
            ] },
        ]}
      />

      {/* ÖZET BAR — sadece kasa seçiliyse */}
      {summary && (
        <div style={{ display: 'flex', gap: 2, padding: '0 0 12px 0', flexWrap: 'wrap' }}>
          {[
            { label: 'Devreden',      val: summary.devreden,  color: 'var(--text-primary)' },
            { label: 'Toplam Giren',  val: summary.total_in,  color: '#16a34a' },
            { label: 'Toplam Cikan',  val: summary.total_out, color: '#dc2626' },
            { label: 'Kalan',         val: summary.balance,   color: summary.balance >= 0 ? '#16a34a' : '#dc2626' },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ flex: '1 1 160px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 'var(--r-md)', padding: '10px 16px' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color, fontFamily: 'monospace' }}>{fmt(val, summary.currency_code)}</div>
            </div>
          ))}
        </div>
      )}

      {summary && filters.date_from && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)', borderRadius: 8, padding: '8px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Devreden Bakiye ({fmtDate(filters.date_from)})</span>
          <span style={{ fontWeight: 700, color: summary.devreden >= 0 ? '#16a34a' : '#dc2626', fontFamily: 'monospace' }}>{fmt(summary.devreden, summary.currency_code)}</span>
        </div>
      )}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="cash-movements-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'document_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.document_no || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'transaction_date', sortable: true, width: 110,
              sortFn: (a, b) => new Date(a.transaction_date || 0) - new Date(b.transaction_date || 0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</span> },
            { id: 'register', header: 'Kasa', accessor: 'register_name', sortable: true, width: 140,
              cell: tx => <span style={{ fontWeight: 600 }}>{tx.register_name}</span> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => <div><div>{tx.description}</div>{tx.notes && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{tx.notes}</div>}</div> },
            { id: 'type', header: 'Tür', accessor: tx => (TX_TYPES[tx.transaction_type] || TX_TYPES.income).label, sortable: true, width: 110,
              cell: tx => { const cfg = TX_TYPES[tx.transaction_type] || TX_TYPES.income; return <span style={{ fontSize: '0.78rem', fontWeight: 600, color: cfg.color }}>{cfg.label}</span>; } },
            { id: 'ref_no', header: 'Ref No', accessor: 'reference_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{tx.reference_no || '—'}</span> },
            { id: 'amount', header: 'Tutar', accessor: tx => parseFloat(tx.amount) || 0, sortable: true, width: 150,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: tx => { const cfg = TX_TYPES[tx.transaction_type] || TX_TYPES.income; return <span style={{ fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>{cfg.sign > 0 ? '+' : '-'}{fmt(tx.amount, tx.currency_code)}</span>; } },
          ]}
        />
      </div>

      {/* DETAY MODAL */}
      {modalMode === 'view' && selectedTx && (() => {
        const txCfg = TX_TYPES[selectedTx.transaction_type] || TX_TYPES.income;
        return (
          <Modal
            icon={null}
            title="Kasa Hareketi"
            size="md"
            onClose={closeModal}
          >
            <CardHero
              icon="💵"
              code={selectedTx.document_no}
              title={txCfg.label}
              subtitle={selectedTx.register_name}
              kpis={[
                selectedTx.amount != null && {
                  label: 'Tutar',
                  value: (txCfg.sign > 0 ? '+' : '-') + fmt(selectedTx.amount, selectedTx.currency_code),
                  color: txCfg.sign > 0 ? 'success' : 'danger',
                  sublabel: selectedTx.currency_code,
                },
              ].filter(Boolean)}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: '0.875rem' }}>
              <div><span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Tarih</span>{fmtDate(selectedTx.transaction_date)}</div>
              <div><span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Kasa</span><strong>{selectedTx.register_name}</strong></div>
              <div style={{ gridColumn: '1 / -1' }}><span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Açıklama</span>{selectedTx.description}</div>
              <div>
                <span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Tutar</span>
                <strong style={{ color: txCfg.color, fontSize: '1rem' }}>{txCfg.sign > 0 ? '+' : '-'}{fmt(selectedTx.amount, selectedTx.currency_code)}</strong>
              </div>
              <div><span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Referans No</span>{selectedTx.reference_no || '—'}</div>
              {selectedTx.notes && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '0.75rem', marginBottom: 2 }}>Not</span><em style={{ color: 'var(--text-secondary)' }}>{selectedTx.notes}</em></div>}
            </div>
          </Modal>
        );
      })()}

      {/* FORM MODAL (add) */}
      {modalMode === 'add' && (
        <Modal
          icon={TAB_ICONS.cash}
          title="Yeni Kasa Hareketi"
          size="md"
          onClose={closeModal}
          cancelAction={{ label: 'Vazgeç', onClick: closeModal }}
          primaryAction={{ label: 'Kaydet', onClick: handleSave, loading: saving }}
        >
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <FormField label="Kasa" required>
              <select value={form.register_id} onChange={e => tf('register_id', e.target.value)}>
                <option value="">Kasa secin</option>
                {registers.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
              </select>
            </FormField>
            <FormField label="Hareket Türü" required>
              <select value={form.transaction_type} onChange={e => tf('transaction_type', e.target.value)}>
                {Object.entries(TX_TYPES).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
              </select>
            </FormField>
            <FormField label="Tarih" required>
              <input type="date" value={form.transaction_date} onChange={e => tf('transaction_date', e.target.value)} />
            </FormField>
            <FormField label="Tutar" required>
              <input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={e => tf('amount', e.target.value)} />
            </FormField>
            <FormField label="Açıklama" required fullWidth>
              <input value={form.description} onChange={e => tf('description', e.target.value)} placeholder="İşlem aciklamasi" />
            </FormField>
            <FormField label="Referans No">
              <input value={form.reference_no} onChange={e => tf('reference_no', e.target.value)} placeholder="Opsiyonel" />
            </FormField>
            <FormField label="Not">
              <input value={form.notes} onChange={e => tf('notes', e.target.value)} placeholder="Opsiyonel" />
            </FormField>
            <FormField label="Personel">
              <select value={form.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <div />
            <FormField label={<>Özel Kod 1 <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special1')}>+</button></>}>
              <select value={form.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special2')}>+</button></>}>
              <select value={form.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
